<script lang="ts" setup>
import { New } from '@/components';
import { AppTypography } from '@/components/base';
import {
  JobListingPayRate,
  JobListingRequiredDocuments,
  JobListingRequiredSchedules,
  JobListingSupportPreference,
} from '@/components/support-seeker';
import { ReportJobListing, SavedJob } from '@/components/support-seeker/forms';
import type { JobListing } from '@/types/JobListing';
import CONFIG from '@/utils/Config';

const { showSnackbar } = useSnackbar();

const props = withDefaults(
  defineProps<{
    jobListingId?: number;
    jobListing?: JobListing;
    isFullyVerified?: boolean;
  }>(),
  {}
);

const emit = defineEmits<{
  isApplied: [value: boolean | undefined];
}>();

const { data, error: serverError, execute: execCreateJob } = useSubmit();
const jobListingData = ref<JobListing | null>(null);

const viewJob = async (): Promise<void> => {
  try {
    const { data } = await useFetchData<any>('/jobs/' + props.jobListingId, {
      method: 'GET',
    });
    if (data.value) {
      jobListingData.value = data.value;
      // Emit the isApplied value after data is loaded
      emit('isApplied', data.value?.is_applied);
      // emit('isSaved', data.value?.is_saved );
    }
  } catch (error) {
    console.error('Error fetching job details:', error);
  }
};

onMounted(() => {
  nextTick(() => {
    if (props.jobListingId) {
      viewJob(props.jobListingId);
    }
  });
});

const showVerificationPrompt = ref(false);

function handleObscuredClick() {
  if (!props.isFullyVerified) {
    showVerificationPrompt.value = true;
  }
}

const addedToSavedList = async (jobId: string): Promise<void> => {
  // jobs/workers-favourite-jobs
  if(jobListingData.value?.is_favourite || props.jobListing?.is_favourite) { 
    try {
      await execCreateJob('jobs/workers-favourite-jobs', {
        method: 'DELETE',
        body: {
          job_id: jobId,
        },
      });
      
      if (data.value) {
        // Update local state immediately after successful deletion
        if (jobListingData.value) {
          jobListingData.value.is_favourite = false;
        }
        if (props.jobListing) {
          props.jobListing.is_favourite = false;
        }
        
        showSnackbar({
          text: data.value?.message || 'Job removed from saved list successfully!',
          state: 'success',
        });
      }

    } catch (error) {
      console.log('error: ', error);
      showSnackbar({
        text: 'Failed to remove job from saved list. Please try again.',
        state: 'error',
      });
    }
  } else {
    try {
      await execCreateJob('jobs/workers-favourite-jobs', {
        method: 'POST',
        body: {
          job_id: jobId,
        },
      });
      
      if (data.value) {
        // Update local state immediately after successful addition
        if (jobListingData.value) {
          jobListingData.value.is_favourite = true;
        }
        if (props.jobListing) {
          props.jobListing.is_favourite = true;
        }
        
        showSnackbar({
          text: data.value?.message || 'Job added to saved list successfully!',
          state: 'success',
        });
      }

    } catch (error) {
      console.log('error: ', error);
      showSnackbar({
        text: 'Failed to add job to saved list. Please try again.',
        state: 'error',
      });
    }
  }
};

const getDayName = (dayNumber: string): string => {
  const dayMap: { [key: string]: string } = {
    '1': 'Monday',
    '2': 'Tuesday', 
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday',
    '8': 'Days and Times to be advised'
  };
  return dayMap[dayNumber] || `Day ${dayNumber}`;
};

const formatDaysAndTimes = (daysTime: any[]): string[] => {
  if (!Array.isArray(daysTime)) return [];
  
  return daysTime.map(day => {
    const dayNumber = Object.keys(day).find(key => key !== 'start_time' && key !== 'end_time');
    const dayName = getDayName(dayNumber || '');
    return dayNumber != '8' && day.start_time && day.end_time ? `${dayName}    ${day.start_time} - ${day.end_time}` : `${dayName}`;
  });
};
</script>

<template>
  <v-toolbar density="compact" class="header-toolbar">
    <v-toolbar-title>
      <div class="d-flex justify-space-between align-center">
        <AppTypography type="title-2" element="h2" class="text-white">
          Job Details
        </AppTypography>
        <SavedJob :disabled="!isFullyVerified" :isSaved="jobListingData?.is_favourite || jobListing?.is_favourite" @click="addedToSavedList(jobListing?.job_id ? jobListing?.job_id : jobListing?.id)" class="back-color" />
        <!-- <ReportJobListing :disabled="!isFullyVerified" @click="addedToSavedList(jobListing?.job_id ? jobListing?.job_id : jobListing?.id)" class="back-color" /> -->
      </div>
    </v-toolbar-title>
  </v-toolbar>
  <div class="pa-6" @click="handleObscuredClick">
    <!-- <div class="mb-6" :class="{ obscured: !isFullyVerified }">
      <AppTypography type="title-3" element="h3" class="mb-3">Job Title</AppTypography>
      <AppTypography type="body-1">{{ jobListing?.title || '--' }}</AppTypography>
    </div> -->
    <div class="mb-6" :class="{ obscured: !isFullyVerified }">
      <AppTypography type="title-3" element="h3" class="mb-3">Job Description</AppTypography>
      <AppTypography type="body-1">{{ jobListing?.description || '--' }}</AppTypography>
    </div>
    <div class="mb-6" :class="{ obscured: !isFullyVerified }">
      <AppTypography type="title-3" element="h3" class="mb-3">Job Location</AppTypography>
      <AppTypography type="body-1">{{ jobListing?.address || '--' }}</AppTypography>
    </div>
    <div class="mb-6" :class="{ obscured: !isFullyVerified }">
      <AppTypography type="title-3" element="h3" class="mb-3">
        Days/Times/Dates
      </AppTypography>
      <div v-if="jobListing.days_time && Array.isArray(jobListing.days_time)">
        <div v-for="(dayTime, index) in formatDaysAndTimes(jobListing.days_time)" :key="index" class="day-time-item">
          {{ dayTime }}
        </div>
      </div>
      <div v-else>
        <AppTypography type="body-1">
          Start Date: {{ jobListing.days_time ? jobListing.days_time.start_date : '-- '}}
        </AppTypography>
        <AppTypography type="body-1">
          End Date: {{ jobListing.days_time ? jobListing.days_time.end_date : '--' }}
        </AppTypography> 
      </div>
    </div>
    <div class="mb-6">
      <AppTypography type="title-3" element="h3" class="mb-3">Support Requirement</AppTypography>
      <AppTypography type="body-1">
        <ul class="list-disc list-inside ml-7" v-if="(jobListing as any)?.support_requirements?.length > 0">
          <li v-for="(requirement, index) in (jobListing as any)?.support_requirements" :key="index" >
            {{ requirement.name }}
          </li>
        </ul>
        <AppTypography type="body-1" v-else>--</AppTypography>
      </AppTypography>
    </div>
    <div class="mb-6">
      <AppTypography type="title-3" element="h3" class="mb-3">Qualifications</AppTypography>
      <AppTypography type="body-1">
        <ul class="list-disc list-inside ml-7" v-if="(jobListing as any)?.qualifications?.length > 0">
          <li v-for="(qualification, index) in (jobListing as any)?.qualifications" :key="index">
            {{ qualification.name }}
          </li>
        </ul>
        <AppTypography type="body-1" v-else>--</AppTypography>
      </AppTypography>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showVerificationPrompt" class="modal-overlay" @click.self="showVerificationPrompt = false">
      <div class="modal-content">
        <span class="modal-close" @click="showVerificationPrompt = false" aria-label="Close">&times;</span>
        <div class="modal-message">
          Please complete your profile to see jobs suited to you.
        </div>
      </div>
    </div>
  </Teleport>
</template>
<style lang="scss">

.obscured {
  filter: blur(4px) grayscale(1);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}

.header-toolbar {
  background:  #b69ce0;
}
.back-color {
  background-color: #b69ce0;
}

// Modal styles
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 990000000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 28px;
  max-width: 460px;
  width: 90%;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-close {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
}

.modal-message {
  font-size: 15px;
  color: #333;
  text-align: center;
  line-height: 1.5;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
